import * as React from 'react';
import { FormEvent } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { Button, IconButton, TextField, Theme, createStyles, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { MatterTerminology } from '../../MatterTerminology';
import gql from 'graphql-tag';
import { graphql, Query } from 'react-apollo';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { client } from '../..';
import { showNotification } from '../../App';
import { Message } from './hooks/useMessageQuery';
import { SourceMessage } from '../../common/constants';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        card: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(2.5),
            margin: theme.spacing(0, 4, 2.5, 4),
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(1.5),
        },
        subjectField: {
            flex: 1,
            maxWidth: 280,
        },
        closeButton: {
            marginLeft: 'auto',
        },
        textarea: {
            marginBottom: theme.spacing(1.5),
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing(1),
        },
    })
);

interface NoteSubjectOption {
    stringValue: string;
}

// Function component wrapping the styled/functional bits, so hooks (makeStyles) can be used
// while the class below keeps owning the Apollo mutation/state logic unchanged.
const MessageInputCardBody: React.FC<{
    subjectOptions: string[] | null;
    subjectName: string;
    canCreateMessageSubject: boolean;
    subject: string;
    body: string;
    isSubmitting: boolean;
    canSaveInternal: boolean;
    isUserInternal: boolean;
    onChangeSubject: (value: string | null) => void;
    onChangeBody: (event: FormEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    onCancel: () => void;
    onSend: (isInternal: boolean) => void;
}> = (props) => {
    const classes = useStyles();

    return (
        <div className={classes.card}>
            <div className={classes.header}>
                {props.canCreateMessageSubject && (
                    <Autocomplete
                        className={classes.subjectField}
                        options={props.subjectOptions || []}
                        value={props.subject || null}
                        onChange={(event: React.ChangeEvent<{}>, value: string | null) => props.onChangeSubject(value)}
                        renderInput={(params) => (
                            <TextField {...params} label={props.subjectName} placeholder="Select to view subjects" size="small" />
                        )}
                    />
                )}
                <IconButton size="small" className={classes.closeButton} onClick={props.onCancel} aria-label="Close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>
            <TextField
                className={classes.textarea}
                value={props.body}
                onChange={props.onChangeBody}
                placeholder={MatterTerminology.TypeMessage}
                multiline={true}
                rows={3}
                fullWidth={true}
                variant="outlined"
            />
            <div className={classes.actions}>
                {props.canSaveInternal && (
                    <Button disabled={props.isSubmitting} onClick={() => props.onSend(true)}>
                        Save Matter Note
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    disabled={props.isSubmitting}
                    onClick={() => props.onSend(false)}
                >
                    {props.isUserInternal ? 'Send Message' : 'Send'}
                </Button>
            </div>
        </div>
    );
};

interface MessageInputCardProps extends ExternalProps {
    data: {
        loading?: boolean;
        networkStatus?: number;
        options: {
            matterOptions: {
                canCreateInternalMessage: boolean;
                canCreateMessageSubject: boolean;
            }
        }
        appUser: {
            name: string;
            userId: string;
            isInternal: boolean;
            isInternalAdmin: boolean;
        }
        tenant: {
            sourceSystem: string;
        }
    };
    isSubmitted?: boolean;
}

interface MessageInputCardState {
    subject: string;
    body: string;
    isInternalMsg: boolean;
}

class RvMessageInputCard extends React.Component<MessageInputCardProps, MessageInputCardState> {
    private isSubmitting: boolean;
    // tslint:disable-next-line:no-any
    constructor(props: MessageInputCardProps, context?: any) {
        super(props, context);
        this.state = { subject: '', body: '', isInternalMsg: true };
        this.isSubmitting = false;
        this.handleClick = this.handleClick.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
    }

    public render() {
        if (this.props.data.loading) {
            return <SkeletonLoader variant="form" rows={2} columns={1} />;
        }

        var canCreateMessageSubject = this.props.data.options.matterOptions.canCreateMessageSubject;
        // Check source system. If source system is InProtech, then do not show the subject
        if (this.props.sourceSystem.toLocaleLowerCase() === 'leap') {
            canCreateMessageSubject = false;
        }
        var subjectName = MatterTerminology.Subject;
        if (this.props.data.tenant.sourceSystem.toLocaleLowerCase() === 'inprotech') {
            subjectName = 'Activity';
        }

        return (
            // Was inheriting the app's global default (cache-and-network) - MessageInputCard
            // is always mounted once the Messages tab renders (MUI's Collapse in
            // MatterMessageView.tsx doesn't unmount its children when closed), so this fired
            // on every Messages tab visit regardless of whether the compose panel was open.
            // tslint:disable-next-line:no-any
            <Query<any>
                key="note-subject"
                query={NoteSubjects}
                fetchPolicy="cache-first"
            >
            {({
                // tslint:disable-next-line: no-shadowed-variable
                loading, error, data
            }) => {
                const subjectOptions: string[] | null =
                    data && canCreateMessageSubject && data.noteSubjects
                        ? data.noteSubjects.noteSubject.map((s: NoteSubjectOption) => s.stringValue)
                        : null;

                return (
                    <MessageInputCardBody
                        subjectOptions={subjectOptions}
                        subjectName={subjectName}
                        canCreateMessageSubject={canCreateMessageSubject}
                        subject={this.state.subject}
                        body={this.state.body}
                        isSubmitting={this.isSubmitting}
                        canSaveInternal={this.props.data.options.matterOptions.canCreateInternalMessage}
                        isUserInternal={this.props.isUserInternal}
                        onChangeSubject={this.changeSubject}
                        onChangeBody={this.changeBody}
                        onCancel={this.handleCancel}
                        onSend={(isInternal) => this.handleClick(isInternal)}
                    />
                );
            }}
            </Query>
        );

    }

    private changeSubject = (value: string | null) => {
        this.setState({ subject: value || '' });
    }

    private changeBody = (event: FormEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        var target = event.target as HTMLInputElement;
        this.setState({ body: target.value });
    }

    private handleCancel = () => {
        this.props.toggleVisibility();
    }

    private handleClick = (isInternal: boolean) => {
        this.isSubmitting = true;

        if (this.props.data.options.matterOptions.canCreateMessageSubject &&
            this.state.subject.trim().length === 0) { // added check - should not get here
            showNotification(null, 'Subject cannot be empty', 'warning');
            return;
        }
        if (this.state.body.trim().length === 0) { // added check - should not get here
            showNotification(null, 'Body cannot be empty', 'warning');
            return;
        }

        this.setState({ isInternalMsg: isInternal });

        client.mutate({
            mutation: MessageMutation,
            variables: {
                subject: this.state.subject,
                comment: this.state.body,
                isInternal: this.props.data.options.matterOptions.canCreateInternalMessage && isInternal,
                matterId: this.props.id,
                postedTime: new Date()
            },
            optimisticResponse: {
                data: {
                    createPendingComment: true
                }
            },
        // tslint:disable-next-line: no-any
        }).then((results: any) => {

            const message: Message = {
                date: new Date(),
                subject: this.state.subject ? this.state.subject : '',
                comment: this.state.body ? this.state.body : '',
                pending: true,
                source:  isInternal ? SourceMessage.InternalMessageOnlyFromInternalUser : SourceMessage.ExternalMessageFromClient,
                title: '',
                enteredBy: this.props.data.appUser.name,
                enteredByUserId: this.props.data.appUser.userId
            };

            if (this.props.updateAction) {
                this.props.updateAction(message);
            }

            this.setState({
                subject: '',
                body: '',
                isInternalMsg: true
            });

            this.isSubmitting = false;
            this.props.toggleVisibility();

        // tslint:disable-next-line:no-any
        }).catch((reason: any) => {
            this.isSubmitting = false;
            showNotification(null, reason, 'error');
        });
    }
}

const MessageMutation = gql`mutation
        ($subject: String, $comment: String!, $isInternal: Boolean, $matterId: Int!, $postedTime: DateTime) {
    createPendingComment
        (subject: $subject, comment: $comment, isInternal: $isInternal, matterId: $matterId, postedTime: $postedTime)
}`;

const MessageData = gql`query MessageInputCard($id: Int!) {
    options {
      matterOptions(matterId: $id) {
        canCreateInternalMessage
        canCreateMessageSubject
      }
    }
    appUser {
        name
        userId
        isInternal
        isInternalAdmin
    }
    tenant {
        sourceSystem
    }
}`;

const NoteSubjects = gql`query NoteSubjects
    { noteSubjects
        { noteSubject
            { stringValue }
        }
    } `;

interface ExternalProps {
    id: string;
    sourceSystem: string;
    isUserInternal: boolean;
    toggleVisibility: () => void;
    updateAction: (message: Message) => void;
}

// tslint:disable-next-line:no-any
export const MessageInputCard = graphql<any, ExternalProps, any>
    (MessageData, {
        // Was inheriting the app's global default (cache-and-network) - see the NoteSubjects
        // Query's own comment above for why this component fires on every Messages tab visit.
        // sourceSystem dropped from variables - MessageData only declares $id, so it was never
        // used server-side, just an extra value fragmenting Apollo's cache key if it ever
        // differs between renders (this.props.sourceSystem below still works - it's ExternalProps,
        // merged in by graphql() independently of the query's own variables/result).
        options: ({ id }) => ({
            variables: {
                id: id
            },
            fetchPolicy: 'cache-first'
        })
    })
    (RvMessageInputCard);
